<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {ElOption, ElSelect} from 'element-plus';
import {createObject, deleteObject, listObjects, updateObject} from '@/base/callBackend';
import {showHideLoading} from '@/base/loadingState';
import {CabInitiatief, CabOrganization, uuidOf} from 'common';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import {INITIATIEF_CHANGE_EMIT, INITIATIEF_REMOVE_EMIT} from '@/base/cabConstants';
import InitiatiefStrip from '@/views/admin/InitiatiefStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';

const alle          = ref<CabInitiatief[]>([]);
const organizations = ref<CabOrganization[]>([]);
const kwartaal      = ref<string>('');
const filterActive  = ref<boolean>(false);
const vanKwartaal   = computed<CabInitiatief[]>(() => alle.value.filter(i => kwartaal.value === '' || i.kwartaal === kwartaal.value));
const gefilterd     = ref<CabInitiatief[]>([]);
const kwartalen     = computed<string[]>(() => [...new Set(alle.value.map(i => i.kwartaal))].sort().reverse());

onMounted(async () => {
    showHideLoading(true);
    try {
        organizations.value = await listObjects(CabOrganization);
        alle.value          = await listObjects(CabInitiatief);
        kwartaal.value      = kwartalen.value[0] ?? '';
    } finally {
        showHideLoading(false);
    }
});

//=====================================================================================
async function onRemove(initiatief: CabInitiatief) {
    await deleteObject(initiatief);
    alle.value = alle.value.filter((i) => uuidOf(i) !== uuidOf(initiatief));
}

async function onAdd() {
    filterActive.value         = false;
    const nieuw: CabInitiatief = new CabInitiatief();
    nieuw.kwartaal             = kwartaal.value;
    await createObject(nieuw);
    alle.value = [nieuw, ...alle.value];
}

async function onChange(initiatief: CabInitiatief) {
    const uuid: string = uuidOf(initiatief);
    await updateObject(initiatief);
    alle.value = alle.value.map((i) => uuidOf(i) === uuid ? initiatief : i);
}
</script>

<template>
    <div class="flex flex-col flex-1">
        <div class="flex flex-row flex-1 mb-1 items-center gap-2">
            <PlusButton @click="onAdd()"/>
            <div class="text-2xl flex-1 ml-2" data-testid="data-section-title">
                Initiatieven
            </div>
            <el-select v-model="kwartaal" placeholder="kwartaal" class="w-32" clearable>
                <el-option v-for="q in kwartalen" :key="q" :value="q" :label="q"/>
            </el-select>
            <GeneralFilter v-model:active="filterActive"
                           :cab-list="vanKwartaal"
                           v-model:cab-list-filtered="gefilterd"
                           :id="'InitiatiefList'"
            />
        </div>
        <div class="text-sm text-gray-500 mb-2">
            Een rij per initiatief per kwartaal, zoals in het PMO-bestand. Nieuwe kwartalen komen binnen via Upload.
        </div>
        <InitiatiefStrip
            v-for="initiatief in gefilterd"
            :key="uuidOf(initiatief)"
            :initiatief="initiatief"
            :allOrganizations="organizations"
            v-on:[INITIATIEF_REMOVE_EMIT]="onRemove"
            v-on:[INITIATIEF_CHANGE_EMIT]="onChange"
        />
    </div>
</template>
